import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";

export default function LostFoundPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "lost", item: "", location: "" });
  const [open, setOpen] = useState(false);
  const load = async () => { const { data } = await api.get(`/lost-found?property_id=${propertyId}`); setItems(data.items); };
  useEffect(() => { if (propertyId) load(); }, [propertyId]);
  const submit = async () => {
    if (!form.item) return toast.error("Describe the item");
    await api.post(`/lost-found?property_id=${propertyId}`, form);
    toast.success("Reported");
    setOpen(false); setForm({ type: "lost", item: "", location: "" });
    load();
  };
  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Lost & Found"
        subtitle="Report what you've lost or found."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="lf-new" className="rounded-full bg-zinc-900 hover:bg-zinc-800"><Plus className="h-4 w-4 mr-1" />Report</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Report item</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="found">Found</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Item</Label><Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <Button onClick={submit} className="w-full">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((i) => (
          <Card key={i.id} className="p-5 bg-white">
            <div className="flex items-start gap-3">
              <div className={"h-10 w-10 rounded-md border flex items-center justify-center " + (i.type === "found" ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200")}>
                <Search className={"h-4 w-4 " + (i.type === "found" ? "text-emerald-600" : "text-rose-600")} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{i.type}</Badge>
                  <Badge variant="outline" className="capitalize">{i.status}</Badge>
                </div>
                <div className="font-medium mt-1">{i.item}</div>
                <div className="text-xs text-zinc-500 mt-1">📍 {i.location}</div>
                <div className="text-xs text-zinc-500">Reported by {i.reported_by}</div>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">Nothing reported yet.</div>}
      </div>
    </div>
  );
}
